import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import LiveCaller from '@/LiveCaller'
import ApiService from '@/services/api.services'
import Str from '@/util/Str'
import { useWebSocketStore } from '@/stores/websocket'
import { useAvailabilityStore } from '@/stores/availability'

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v)

// Recursive merge: plain objects merge key-by-key, everything else (arrays,
// primitives) replaces. Returns a new object so the ref assignment is reactive.
function deepMerge(base, patch) {
    const out = isPlainObject(base) ? { ...base } : {}
    for (const [key, value] of Object.entries(patch)) {
        out[key] = isPlainObject(value) && isPlainObject(out[key])
            ? deepMerge(out[key], value)
            : value
    }
    return out
}

// The API payload plus the two behaviors the app uses on the auth user
// (replaces the old vue-api-query User model).
function createUser(data) {
    return {
        ...data,
        can(permissions, hasAnyPermission = true) {
            if (typeof permissions === 'function') {
                return permissions(this)
            }
            if (Array.isArray(permissions)) {
                if (permissions.length === 0) {
                    return true
                }
                if (hasAnyPermission) {
                    return permissions.some((permission) => this.can(permission))
                }
                return permissions.every((permission) => this.can(permission))
            }
            return this.all_permissions.some((permission) => Str.is(permissions, permission))
        },
        updateStatus(status) {
            return LiveCaller.request().patch('/user/status', { status }).then((response) => response)
        },
    }
}

export const useAuthStore = defineStore('auth', () => {
    const user = ref(null)
    const loading = ref(true)

    const isAuthenticated = computed(() => user.value !== null)

    let loadPromise

    function load(forceReload = false) {
        if (typeof loadPromise !== 'undefined' && (loading.value || !forceReload)) {
            return loadPromise
        }

        loadPromise = new Promise((resolve, reject) => {
            loading.value = true
            if (!forceReload && user.value) {
                resolve(user.value)
                return
            }
            LiveCaller.request('/user').then((response) => {
                user.value = createUser(response.data.data)

                if (user.value.availability) {
                    try {
                        const availabilityStore = useAvailabilityStore()
                        availabilityStore.applyState(user.value.availability)
                    } catch {
                        // Pinia may not be initialized yet in some edge cases
                    }
                }

                resolve(user.value)
            }).catch((e) => {
                reject(e)
            })
        }).finally(() => {
            loading.value = false
        })

        return loadPromise
    }

    async function register(userData) {
        await LiveCaller.request().post('/user/register', userData)
        return { success: true }
    }

    async function forgotPassword(formData) {
        return LiveCaller.request().post('/password/email', formData)
    }

    async function logOut() {
        try {
            const websocketStore = useWebSocketStore()
            websocketStore.destroy()
        } catch (e) {
            // Ignore if store not initialized
        }
        user.value = null
        // This will clear oauth tokens and redirect to /signin
        await ApiService.logOut()
    }

    function isWhiteListedIp() {
        LiveCaller.request('/account/is-ip-whitelisted')
            .then((res) => {
                if (!res.data.ip_white_listed) {
                    logOut()
                }
            })
            .catch(() => {})
    }

    function setUser(value) {
        user.value = value
    }

    // Merge a roaming UI-settings patch into the cached user so preferences
    // (column order/visibility, display toggles) survive an in-session
    // component remount — reads otherwise fall back to the stale boot-time
    // snapshot from load() and the change appears to revert until a reload.
    // Pass the patch shaped like the READ path, e.g.
    // { reports: { conversations: { columns: { order } } } }.
    function mergeUiSettings(patch) {
        if (!user.value || !patch) return
        user.value.settings = user.value.settings || {}
        user.value.settings.ui = deepMerge(user.value.settings.ui || {}, patch)
    }

    return {
        user,
        loading,
        isAuthenticated,
        load,
        register,
        forgotPassword,
        logOut,
        isWhiteListedIp,
        setUser,
        mergeUiSettings,
    }
})
